import Header from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useCreateEvent, Event } from "@/hooks/useEvents";
import { useRoutes } from "@/hooks/useRoutes";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { ArrowLeft, Lock, Mountain } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import PhotoUpload from "@/components/PhotoUpload";
import { useEventPhotoUpload } from "@/hooks/useEventPhotoUpload";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

const CreateEvent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canAccess } = useSubscription();
  const { routes } = useRoutes();
  const createEventMutation = useCreateEvent();
  const { uploadPhotos, isUploading } = useEventPhotoUpload();

  const canAccessEvents = canAccess('organizer');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    event_type: 'ride' as Event['event_type'],
    route_id: '',
    start_date: '',
    estimated_duration_hours: '',
    meeting_point_description: '',
    max_participants: '',
    equipment_requirements: [] as string[],
    notes: '',
    waiver_required: false,
    waiver_text: '',
  });

  const defaultWaiverTemplate = `ASSUMPTION OF RISK, WAIVER OF LIABILITY, AND INDEMNITY AGREEMENT

By participating in this off-road event, I acknowledge and accept the following:

1. NATURE OF ACTIVITY
I understand that off-road vehicle operation involves inherent risks including, but not limited to: vehicle rollovers, collisions, mechanical failures, encounters with wildlife, extreme weather conditions, remote locations with limited communication and emergency services, and the possibility of serious injury or death.

2. ASSUMPTION OF RISK
I voluntarily choose to participate in this event with full knowledge of the dangers involved. I accept and assume all risks of injury, death, or property damage, whether caused by the negligence of the organizers, other participants, or otherwise.

3. WAIVER OF LIABILITY
I hereby release, waive, discharge, and covenant not to sue Airing Down (the platform provider), the event organizers, landowners, sponsors, volunteers, and all affiliated parties from any and all liability for any loss, damage, injury, or death to my person or property arising from my participation in this event.

4. RELEASE OF PLATFORM PROVIDER
In addition to releasing the event organizers, I specifically release and hold harmless Airing Down, its owners, operators, employees, contractors, and affiliates from any liability arising from:
- My use of the Airing Down platform to discover, register for, or communicate about this event
- Any technical issues, errors, or omissions in event information displayed on the platform
- Any miscommunication or misunderstanding arising from platform features
- The actions or conduct of other platform users
I understand that Airing Down is merely a technology platform that facilitates connections between event organizers and participants, and does not organize, oversee, or assume responsibility for any events listed on the platform.

5. INSURANCE AND MEDICAL EXPENSES
I confirm that I carry adequate health and vehicle insurance coverage. I agree to be financially responsible for any medical expenses, rescue operations, or property damage resulting from my participation.

6. RULES AND REGULATIONS
I agree to:
- Operate my vehicle safely and responsibly at all times
- Follow all instructions from event organizers and land managers
- Stay on designated trails and respect private property
- Carry required safety equipment
- Possess valid driver's license, vehicle registration, and insurance
- Not participate under the influence of alcohol or drugs

7. INDEMNIFICATION
I agree to indemnify and hold harmless Airing Down, the event organizers, and all affiliated parties from any claims, actions, or losses arising from my participation in this event or use of the Airing Down platform.

8. MEDIA RELEASE
I grant permission for event organizers to use photographs or video footage taken during the event for promotional purposes.

I HAVE READ THIS AGREEMENT CAREFULLY AND UNDERSTAND ITS CONTENTS. I AM AWARE THAT THIS IS A RELEASE OF LIABILITY AND A CONTRACT AND I SIGN IT OF MY OWN FREE WILL.`;

  const handleUseTemplate = () => {
    setFormData({ ...formData, waiver_text: defaultWaiverTemplate });
    toast.success('Default waiver template loaded');
  };

  const [equipmentInput, setEquipmentInput] = useState('');
  const [photos, setPhotos] = useState<Array<{ file: File; preview: string; id: string }>>([]);

  // Redirect non-organizers
  if (user && !canAccessEvents) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            <Card className="border-primary/20">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <Lock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl">Event Creation is an Organizer Feature</CardTitle>
                <CardDescription className="text-base mt-2">
                  Create and host events for your community with our Events feature.
                  Available exclusively for Organizer tier members.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
                  <p className="font-medium text-foreground mb-2">Coming Soon!</p>
                  <p>
                    The Organizer tier is currently in development. Join the waitlist to be notified
                    when it launches.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button onClick={() => navigate('/pricing')} variant="outline">
                    <Mountain className="h-4 w-4 mr-2" />
                    View Plans
                  </Button>
                  <Button onClick={() => navigate('/dashboard')}>
                    Back to Dashboard
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="container mx-auto px-4 py-8">
          <p className="text-center text-muted-foreground">
            Please sign in to create events
          </p>
        </main>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user) {
      toast.error('You must be logged in to create an event');
      return;
    }

    // Upload photos first if any
    let photoUrls: string[] = [];
    if (photos.length > 0) {
      toast.info('Uploading photos...');
      photoUrls = await uploadPhotos(photos.map(p => p.file), user.id);
    }

    const eventData = {
      title: formData.title,
      description: formData.description || undefined,
      event_type: formData.event_type,
      route_id: formData.route_id || undefined,
      start_date: new Date(formData.start_date).toISOString(),
      estimated_duration_hours: formData.estimated_duration_hours ? Number(formData.estimated_duration_hours) : undefined,
      meeting_point_description: formData.meeting_point_description || undefined,
      max_participants: formData.max_participants ? Number(formData.max_participants) : undefined,
      equipment_requirements: formData.equipment_requirements.length > 0 ? formData.equipment_requirements : undefined,
      notes: formData.notes || undefined,
      photo_urls: photoUrls.length > 0 ? photoUrls : undefined,
      waiver_required: formData.waiver_required,
      waiver_text: formData.waiver_required && formData.waiver_text ? formData.waiver_text : undefined,
    };

    createEventMutation.mutate(eventData, {
      onSuccess: (event) => {
        navigate(`/events/${event.id}`);
      },
    });
  };

  const addEquipment = () => {
    if (equipmentInput.trim() && !formData.equipment_requirements.includes(equipmentInput.trim())) {
      setFormData({
        ...formData,
        equipment_requirements: [...formData.equipment_requirements, equipmentInput.trim()]
      });
      setEquipmentInput('');
    }
  };

  const removeEquipment = (item: string) => {
    setFormData({
      ...formData,
      equipment_requirements: formData.equipment_requirements.filter(eq => eq !== item)
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-8 max-w-2xl">
        <Button variant="ghost" onClick={() => navigate('/events')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Events
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Create Event</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Event Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                  placeholder="Saturday Morning Trail Run"
                />
              </div>

              <div>
                <Label htmlFor="event_type">Event Type *</Label>
                <Select 
                  value={formData.event_type} 
                  onValueChange={(value: Event['event_type']) => setFormData({ ...formData, event_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ride">Ride</SelectItem>
                    <SelectItem value="garage_day">Garage Day</SelectItem>
                    <SelectItem value="cleanup">Cleanup</SelectItem>
                    <SelectItem value="meetup">Meetup</SelectItem>
                    <SelectItem value="training">Training</SelectItem>
                    <SelectItem value="overlanding_trip">Overlanding Trip</SelectItem>
                    <SelectItem value="competition">Competition</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="route">Route (Optional)</Label>
                <Select 
                  value={formData.route_id || undefined} 
                  onValueChange={(value) => setFormData({ ...formData, route_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a route (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {routes.map((route) => (
                      <SelectItem key={route.id} value={route.id}>
                        {route.name} {route.length_miles && `(${route.length_miles.toFixed(1)} mi)`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="start_date">Start Date & Time *</Label>
                <Input
                  id="start_date"
                  type="datetime-local"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label htmlFor="meeting_point">Meeting Point</Label>
                <Input
                  id="meeting_point"
                  value={formData.meeting_point_description}
                  onChange={(e) => setFormData({ ...formData, meeting_point_description: e.target.value })}
                  placeholder="Trailhead parking lot on Main St"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration (hours)</Label>
                  <Input
                    id="duration"
                    type="number"
                    step="0.5"
                    min="0"
                    value={formData.estimated_duration_hours}
                    onChange={(e) => setFormData({ ...formData, estimated_duration_hours: e.target.value })}
                    placeholder="3"
                  />
                </div>
                <div>
                  <Label htmlFor="max_participants">Max Participants</Label>
                  <Input
                    id="max_participants"
                    type="number"
                    min="1"
                    value={formData.max_participants}
                    onChange={(e) => setFormData({ ...formData, max_participants: e.target.value })}
                    placeholder="10"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="equipment">Equipment Requirements</Label>
                <div className="flex gap-2 mb-2">
                  <Input
                    id="equipment"
                    value={equipmentInput}
                    onChange={(e) => setEquipmentInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addEquipment())}
                    placeholder="e.g., Helmet, Water, Spare tire"
                  />
                  <Button type="button" onClick={addEquipment} variant="secondary">Add</Button>
                </div>
                {formData.equipment_requirements.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.equipment_requirements.map((item) => (
                      <div key={item} className="flex items-center gap-1 bg-secondary text-secondary-foreground px-3 py-1 rounded-md text-sm">
                        {item}
                        <button
                          type="button"
                          onClick={() => removeEquipment(item)}
                          className="ml-1 hover:text-destructive"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe your event..."
                  rows={4}
                />
              </div>

              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="What to bring, special requirements, etc."
                  rows={3}
                />
              </div>

              <PhotoUpload
                photos={photos}
                onPhotosChange={setPhotos}
                maxPhotos={10}
              />

              <div className="space-y-4 pt-4 border-t">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="waiver-required" className="text-base">
                      Require Waiver
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Participants must accept a waiver before RSVPing
                    </p>
                  </div>
                  <Switch
                    id="waiver-required"
                    checked={formData.waiver_required}
                    onCheckedChange={(checked) => setFormData({ ...formData, waiver_required: checked })}
                  />
                </div>

                {formData.waiver_required && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label htmlFor="waiver-text">Waiver Text *</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleUseTemplate}
                      >
                        Use Default Template
                      </Button>
                    </div>
                    <Textarea
                      id="waiver-text"
                      value={formData.waiver_text}
                      onChange={(e) => setFormData({ ...formData, waiver_text: e.target.value })}
                      placeholder="Enter the waiver terms and conditions or click 'Use Default Template' to load a comprehensive liability waiver..."
                      rows={8}
                      required={formData.waiver_required}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      This waiver will be shown to participants when they RSVP
                    </p>
                  </div>
                )}
              </div>

              <Button 
                type="submit" 
                className="w-full" 
                disabled={createEventMutation.isPending || isUploading}
              >
                {isUploading ? 'Uploading photos...' : createEventMutation.isPending ? 'Creating...' : 'Create Event'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default CreateEvent;
